// BOLDE-owned (lp) — safe to edit on the draft; follow lp/docs/NAMING-STANDARD.md.
/*! lp-swap.js — ad-congruent hero swap for BOLDE LP Factory pages.
 *
 *  WHAT THIS IS
 *  A tiny, self-contained client-side script that lets ONE published LP render
 *  many ad-congruent variations. The ad's URL carries either an opaque immutable
 *  variant id (?lpv=<id>) or a STABLE alias (?lpa=<alias>, v3); this script
 *  fetches an immutable JSON variant from the Storage CDN (resolving the alias's
 *  mutable {v:1,id} pointer first) and swaps text/image on the elements the theme
 *  marked with [data-lp-slot="…"]. Shopify Liquid can't read the query string and
 *  the CDN serves the same cached HTML to everyone, so the swap MUST happen here.
 *
 *  ALIAS INDIRECTION (v3, 2026-07-18): variants stay immutable; the alias doc
 *  `_alias/<alias>.json` is the ONE mutable pointer, repointed server-side by the
 *  audited lp.alias.set op ("push forward" = save new variant + repoint; rollback
 *  = repoint back). It rides the same ?t= minute cache-bust as the killswitch
 *  (≤60s repoint SLA). ?lpv= WINS when both params are present (explicit pin).
 *  The kill check always runs against the RESOLVED id — killing a variant darkens
 *  every alias pointing at it.
 *
 *  TWO DIRECTIONS OF FAILURE, ON PURPOSE:
 *   - The BASE PAGE fails OPEN: any error / no param / bad data / network / timeout
 *     (incl. a missing or malformed alias doc) leaves the default (no-parameter)
 *     page — ~100% of paid traffic — byte-identical to no-script. Every early
 *     return + the whole-body try/catch + promise .catch preserve this.
 *   - The KILL MECHANISM fails CLOSED: a killed OR unverifiable variant must never
 *     render. The killswitch requires a positive health sentinel (v:1); anything
 *     malformed/missing/timed-out → the variant does not apply (default shows).
 *
 *  HARD CONTRACT:
 *   1. INERT WITHOUT PARAMS — neither ?lpv= nor ?lpa= → zero DOM mutation, zero
 *      network, no listeners.
 *   2. NEVER innerHTML / eval. Text via textContent (leaf elements only); images via
 *      setAttribute behind an https + host-allowlist + no-credentials gate.
 *   3. PRICE / OFFER / CLAIM ELEMENTS ARE NOT SLOTS (Meta anti-cloaking shield). Only
 *      manifest-declared slots apply; the variant's slot TYPE must match the manifest.
 *   4. IDEMPOTENT + re-applies on shopify:section:load (OS 2.0 re-renders section HTML
 *      into the DOM without re-running load-time JS).
 *
 *  Config (own-property reads only, so a polluted Object.prototype can't hijack it):
 *  window.__lpSwap = { storeBase, param?, aliasParam?, imageHosts?, slots?,
 *  maxTextLength?, timeoutMs? } emitted by snippets/lp-swap.liquid from
 *  lp-swap-manifest.json (one source of truth).
 */
(function () {
  "use strict";
  try {
    var cfg = window.__lpSwap || {};
    var HOP = Object.prototype.hasOwnProperty;
    function opt(k, d) {
      return HOP.call(cfg, k) ? cfg[k] : d;
    } // F6: own-property reads (prototype-pollution safe)

    var PARAM = opt("param", "lpv");
    var ALIAS_PARAM = opt("aliasParam", "lpa");

    // ---- 1. INERT WITHOUT PARAMS (before any DOM read/write, fetch, or listener) --
    // ?lpv= (pinned immutable id) WINS over ?lpa= (stable alias) when both appear.
    var id = null;
    var alias = null;
    try {
      var qs = new URLSearchParams(window.location.search);
      id = qs.get(PARAM);
      if (!id) alias = qs.get(ALIAS_PARAM);
    } catch (e) {
      return;
    }
    if (!id && !alias) return; // default traffic: nothing happens, always.

    // F10: run once even if the snippet is accidentally rendered twice.
    if (window.__lpSwapRan) return;
    window.__lpSwapRan = 1;

    // ---- 2. Validate tokens — opaque; leading alnum reserves the "_control" namespace (F9) --
    var ID_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{7,63}$/;
    if (id) {
      if (!ID_RE.test(id)) return;
    } else if (!/^[A-Za-z0-9][A-Za-z0-9_-]{2,63}$/.test(alias)) {
      return;
    }

    var storeBase = opt("storeBase", "");
    var STORE = typeof storeBase === "string" ? storeBase.replace(/\/+$/, "") : "";
    if (!STORE) return;

    var ih = opt("imageHosts", null);
    var IMG_HOSTS = Array.isArray(ih) && ih.length ? ih : ["cdn.shopify.com", "boldebottle.com"];
    var st = opt("slots", null);
    var SLOT_TYPES = st && typeof st === "object" ? st : null; // manifest slot→type contract (F3)
    var MAXLEN = typeof opt("maxTextLength", null) === "number" ? cfg.maxTextLength : 200; // F3 clamp
    var ml = opt("maxLengths", null);
    var MAXLENS = ml && typeof ml === "object" ? ml : null; // per-slot clamp (manifest v2); MAXLEN is the fallback
    // v3: slots the MANIFEST declares as first-p writers (write only the
    // wrapper's leading childless <p>, preserving trailing siblings). The
    // runtime carries its OWN allowlist of first-p-CAPABLE slots (Sol review
    // 2026-07-18 HIGH-2): config can SELECT from it, never extend it — a
    // malformed/hostile firstP entry (e.g. a listicle text slot) must not
    // regress the v2 multi-paragraph skip. Adding a future first-p slot means
    // updating BOTH the manifest and this list (they ship together).
    var FIRSTP_CAPABLE = { callout_1_text: 1 };
    var fp = opt("firstP", null);
    var FIRST_P = {};
    if (Array.isArray(fp)) {
      for (var fi = 0; fi < fp.length; fi++) {
        if (typeof fp[fi] === "string" && HOP.call(FIRSTP_CAPABLE, fp[fi])) FIRST_P[fp[fi]] = 1;
      }
    }
    var TIMEOUT = typeof opt("timeoutMs", null) === "number" ? cfg.timeoutMs : 3000; // F7

    var variant = null; // populated after fetch; re-read on section:load

    // ---- helpers (hoisted) ----------------------------------------------
    function fetchJSON(url) {
      var ctl = "AbortController" in window ? new AbortController() : null; // F7 timeout
      var timer = ctl ? setTimeout(function () { ctl.abort(); }, TIMEOUT) : null;
      function clear() { if (timer) clearTimeout(timer); }
      return fetch(url, ctl ? { signal: ctl.signal } : undefined).then(
        function (r) {
          if (!r.ok) { clear(); throw new Error("HTTP " + r.status); }
          return r.json().then(function (j) { clear(); return j; });
        },
        function (e) { clear(); throw e; }
      );
    }

    function safeImageUrl(u) {
      if (typeof u !== "string") return null;
      var url;
      try { url = new URL(u, window.location.href); } catch (e) { return null; }
      if (url.protocol !== "https:") return null;
      if (url.username || url.password || url.port) return null; // F8: no credentials / odd ports
      for (var i = 0; i < IMG_HOSTS.length; i++) {
        var h = IMG_HOSTS[i];
        if (url.hostname === h || url.hostname.endsWith("." + h)) return url.href;
      }
      return null;
    }

    function safeSrcset(ss) {
      if (typeof ss !== "string" || !ss.trim()) return false; // F11: empty-after-trim = absent
      var parts = ss.split(",");
      for (var i = 0; i < parts.length; i++) {
        var token = parts[i].trim();
        if (!token) continue;
        var u = token.split(/\s+/)[0];
        if (u && !safeImageUrl(u)) return false;
      }
      return true;
    }

    // Applies one slot; returns the count of DOM nodes actually mutated (F2).
    function applySlot(key, slot) {
      if (!slot || typeof slot !== "object") return 0;
      if (!/^[A-Za-z0-9_-]+$/.test(key)) return 0; // safe attribute-selector charset
      // F3 hardened (Sol review 2026-07-17 BLOCKER): the manifest allowlist is
      // REQUIRED and unknown keys fail CLOSED. Before v2 an unknown key merely
      // found no hooks; the index-keyed hooks would let un-manifested keys
      // (e.g. review_5_text on a 6-card page) bypass the store's compliance
      // gate entirely. Own-property check keeps prototype pollution out.
      if (!SLOT_TYPES || !HOP.call(SLOT_TYPES, key) || SLOT_TYPES[key] !== slot.type) return 0;
      var nodes = document.querySelectorAll('[data-lp-slot="' + key + '"]');
      var n = 0;
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        if (slot.type === "text") {
          if (typeof slot.value !== "string") continue;
          var target = el;
          if (el.children && el.children.length) {
            // F3 stands: never wipe an arbitrary subtree. TWO narrow manifest/theme-
            // declared exceptions, both writing into a childless <p> so styling
            // survives: (v2) a wrapper whose ENTIRE content is a single childless <p>
            // (single-paragraph richtext, e.g. listicle copy — multi-paragraph still
            // skips, deliberately); (v3, opt-in per slot via the manifest's firstP
            // list) a wrapper whose LEADING child is a childless <p> — write only
            // that intro paragraph, preserving trailing siblings (e.g. the callout's
            // feature <ul>). Anything else still skips.
            if (HOP.call(FIRST_P, key)) {
              if (
                el.children[0].tagName !== "P" ||
                (el.children[0].children && el.children[0].children.length)
              ) continue;
              target = el.children[0];
            } else if (
              el.children.length !== 1 ||
              el.children[0].tagName !== "P" ||
              (el.children[0].children && el.children[0].children.length)
            ) {
              continue;
            } else {
              target = el.children[0];
            }
          }
          var lim = MAXLENS && HOP.call(MAXLENS, key) && typeof MAXLENS[key] === "number" && MAXLENS[key] > 0
            ? MAXLENS[key]
            : MAXLEN; // per-slot clamp (v2), global fallback
          target.textContent = slot.value.length > lim ? slot.value.slice(0, lim) : slot.value;
          n++;
        } else if (slot.type === "image") {
          var img = el.tagName === "IMG" ? el : el.querySelector("img");
          if (!img) continue;
          var src = safeImageUrl(slot.src);
          if (!src) continue; // reject non-allow-listed image → keep the default
          // F5: a <picture> ancestor's <source>s outrank img attrs — drop them (variant loads only).
          var pic = img.parentElement;
          if (pic && pic.tagName === "PICTURE") {
            var srcs = pic.querySelectorAll("source");
            for (var s = 0; s < srcs.length; s++) srcs[s].parentNode.removeChild(srcs[s]);
          }
          if (typeof slot.alt === "string") img.setAttribute("alt", slot.alt.slice(0, 300));
          if (safeSrcset(slot.srcset)) {
            img.setAttribute("srcset", slot.srcset);
            if (typeof slot.sizes === "string") img.setAttribute("sizes", slot.sizes);
          } else {
            img.removeAttribute("srcset");
          }
          img.setAttribute("src", src);
          n++;
        }
      }
      return n;
    }

    function applyAll() {
      if (!variant || !variant.slots || typeof variant.slots !== "object") return 0;
      var keys = Object.keys(variant.slots),
        total = 0;
      for (var i = 0; i < keys.length; i++) {
        try { total += applySlot(keys[i], variant.slots[keys[i]]); } catch (e) {}
      }
      return total;
    }

    function applyAndSignal() {
      var applied = applyAll();
      try {
        // Telemetry hook: heatmap's theme tracker listens for this → lp_variant_rendered.
        // `applied` (F2) lets the canary assert real mutations, not mere event presence.
        document.dispatchEvent(
          new CustomEvent("lp:variant-applied", {
            // id = the RESOLVED variant id; alias = the ?lpa= key (null on ?lpv= loads).
            detail: { id: id, alias: alias, v: +(variant && variant.v) || 1, applied: applied },
          })
        );
      } catch (e) {}
    }

    function whenReady(fn) {
      // F4: don't apply mid-parse (would produce a variant-headline / default-image hybrid).
      if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", fn, { once: true });
      else fn();
    }

    // Require the FULL, well-formed killswitch shape; ANY malformation fails CLOSED
    // (a malformed kill doc must never let a variant through — A3 review HIGH-1).
    // The store's lp_killswitch_set always writes exactly {v:1, all:<bool>, ids:[...]}.
    // Returns true when the given variant id must NOT render.
    function killBlocks(kill, vid) {
      if (
        !kill || typeof kill !== "object" || kill.v !== 1 ||
        typeof kill.all !== "boolean" || !Array.isArray(kill.ids)
      ) return true;
      if (kill.all) return true; // global kill
      return kill.ids.indexOf(vid) !== -1; // this variant killed
    }

    function acceptVariant(v) {
      if (!v || typeof v !== "object" || !v.slots) return; // malformed variant → default
      variant = v;
      whenReady(applyAndSignal);
    }

    // ---- 3. Kill switch (fail-CLOSED, F1) + variant/alias fetch, in parallel ---
    var bust = "?t=" + Math.floor(Date.now() / 60000); // ≤60s SLA (kill + alias repoint)
    var kURL = STORE + "/_killswitch.json" + bust;
    if (id) {
      // Pinned immutable id: variant + killswitch in parallel (the v2 path, unchanged).
      Promise.all([fetchJSON(STORE + "/" + id + ".json"), fetchJSON(kURL)])
        .then(function (res) {
          if (killBlocks(res[1], id)) return;
          acceptVariant(res[0]);
        })
        .catch(function () {
          /* any failure (incl. kill config / timeout) → default page intact */
        });
    } else {
      // Stable alias: resolve the mutable `_alias/<alias>.json` pointer ({v:1, id})
      // + killswitch in parallel, then fetch the RESOLVED immutable variant. The
      // alias doc must be exactly well-formed and its id must pass the same gate as
      // ?lpv= — anything else fails OPEN for the base page. The kill check runs
      // against the resolved id, so killing a variant darkens every alias on it.
      Promise.all([fetchJSON(STORE + "/_alias/" + alias + ".json" + bust), fetchJSON(kURL)])
        .then(function (res) {
          var a = res[0];
          if (
            !a || typeof a !== "object" || a.v !== 1 ||
            typeof a.id !== "string" || !ID_RE.test(a.id)
          ) return;
          if (killBlocks(res[1], a.id)) return;
          id = a.id; // the resolved variant id (telemetry + later re-applies read this)
          return fetchJSON(STORE + "/" + id + ".json").then(acceptVariant);
        })
        .catch(function () {
          /* any failure (incl. alias resolution) → default page intact */
        });
    }

    // ---- 4. Re-apply after an OS 2.0 section re-render (idempotent, no re-signal) --
    document.addEventListener("shopify:section:load", function () {
      if (variant) {
        try { applyAll(); } catch (e) {}
      }
    });
  } catch (e) {
    /* last resort: never let this script break the base page */
  }
})();
